import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

interface UserProfile {
  id: string;
  user_id: string;
  username: string;
  display_name: string | null;
  bio: string | null;
  avatar_url: string | null;
  website: string | null;
  location: string | null;
  is_private: boolean;
  posts_count: number;
  followers_count: number;
  following_count: number;
  pets_count: number;
  created_at: string;
}

interface Pet {
  id: string;
  user_id: string;
  name: string;
  species: string;
  breed: string | null;
  age: number | null;
  gender: string | null;
  description: string | null;
  avatar_url: string | null;
  posts_count: number;
  created_at: string;
  is_public: boolean;
}

interface Post {
  id: string;
  content: string | null;
  image_url: string | null;
  likes_count: number;
  comments_count: number;
  created_at: string;
  pet_id: string | null;
  pets: {
    name: string;
    avatar_url: string | null;
  } | null;
}

export const useUserProfile = (username: string) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [pets, setPets] = useState<Pet[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [canViewContent, setCanViewContent] = useState(false);

  useEffect(() => {
    if (username) {
      fetchUserProfile();
    }
  }, [username, user]);

  useEffect(() => {
    if (profile && user) {
      fetchUserPets();
      fetchUserPosts();
    }
  }, [profile, user, canViewContent]);

  const fetchUserProfile = async () => {
    try {
      // First get the profile by username - always allow viewing profile info
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('username', username)
        .single();

      if (profileError) throw profileError;

      if (!profileData) {
        setProfile(null);
        setLoading(false);
        return;
      }

      // Check if this is the current user's profile
      const isOwn = user?.id === profileData.user_id;
      setIsOwnProfile(isOwn);

      // Determine if we can view content
      let canView = isOwn || !profileData.is_private;
      
      // If private profile and not owner, check if following
      if (!canView && user) {
        const { data: followData } = await supabase
          .from('follows')
          .select('id')
          .eq('follower_id', user.id)
          .eq('following_id', profileData.user_id)
          .maybeSingle();
        
        canView = !!followData;
      }
      
      setCanViewContent(canView);

      // Get real counts (only public content if not following private account)
      const [postsCount, petsCount, followersCount, followingCount] = await Promise.all([
        supabase
          .from('posts')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', profileData.user_id)
          .eq('is_public', true),
        supabase
          .from('pets')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', profileData.user_id)
          .eq('is_public', true),
        supabase
          .from('follows')
          .select('id', { count: 'exact', head: true })
          .eq('following_id', profileData.user_id),
        supabase
          .from('follows')
          .select('id', { count: 'exact', head: true })
          .eq('follower_id', profileData.user_id),
      ]);

      setProfile({
        ...profileData,
        posts_count: postsCount.count || 0,
        pets_count: petsCount.count || 0,
        followers_count: followersCount.count || 0,
        following_count: followingCount.count || 0,
      });
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPets = async () => {
    if (!profile || !canViewContent) {
      setPets([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('pets')
        .select('*')
        .eq('user_id', profile.user_id)
        .eq('is_public', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get post count for each pet
      const petsWithCounts = await Promise.all(
        (data || []).map(async (pet) => {
          const { count } = await supabase
            .from('posts')
            .select('id', { count: 'exact', head: true })
            .eq('pet_id', pet.id)
            .eq('is_public', true);
          
          return {
            ...pet,
            posts_count: count || 0,
          };
        })
      );

      setPets(petsWithCounts);
    } catch (error) {
      console.error('Error fetching user pets:', error);
      setPets([]);
    }
  };

  const fetchUserPosts = async () => {
    if (!profile || !canViewContent) {
      setPosts([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('posts')
        .select(`
          *,
          pets (name, avatar_url)
        `)
        .eq('user_id', profile.user_id)
        .eq('is_public', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts((data as any) || []);
    } catch (error) {
      console.error('Error fetching user posts:', error);
      setPosts([]);
    }
  };

  return {
    profile,
    pets,
    posts,
    loading,
    isOwnProfile,
    canViewContent,
    refetchProfile: fetchUserProfile,
    refetchPets: fetchUserPets,
    refetchPosts: fetchUserPosts,
  };
};